import React, { useCallback, useEffect, useState } from 'react';
import { getNews } from '../../lib/api';
import { adminCreateNews, adminUpdateNews, adminDeleteNews } from '../../lib/adminApi';
import { Plus, Pencil, Trash2, X, Loader2, Newspaper } from 'lucide-react';

const emptyForm = { title: '', date: '', image: '', excerpt: '' };

const AdminNews = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(() => {
    setLoading(true);
    getNews().then(setItems).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setEditing(null);
    const today = new Date().toISOString().slice(0, 10);
    setForm({ ...emptyForm, date: today });
    setError('');
    setEditorOpen(true);
  };
  const openEdit = (n) => {
    setEditing(n);
    setForm({ title: n.title, date: n.date, image: n.image, excerpt: n.excerpt });
    setError('');
    setEditorOpen(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      if (editing) await adminUpdateNews(editing.id, form);
      else await adminCreateNews(form);
      setEditorOpen(false);
      load();
    } catch (err) {
      setError(err?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (n) => {
    if (!window.confirm(`Delete "${n.title}"?`)) return;
    await adminDeleteNews(n.id);
    load();
  };

  const inputCls = 'w-full bg-zinc-950 border border-zinc-800 text-white px-3 py-2 rounded text-sm focus:outline-none focus:border-[#909090]';

  return (
    <div className="p-8">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
        <div className="flex items-center gap-3">
          <Newspaper className="text-[#909090]" size={26}/>
          <h1 className="text-3xl font-bold">News</h1>
        </div>
        <button onClick={openCreate} className="inline-flex items-center gap-2 bg-[#909090] hover:bg-[#a8a8a8] text-black font-semibold px-5 py-2.5 rounded transition-colors">
          <Plus size={18}/> Add article
        </button>
      </div>

      {loading ? (
        <div className="py-20 text-center text-zinc-500">Loading...</div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((n) => (
            <div key={n.id} className="bg-zinc-950 border border-zinc-900 rounded-lg overflow-hidden">
              <div className="aspect-[16/10] bg-gradient-to-br from-zinc-900 to-black">
                <img src={n.image} alt={n.title} className="w-full h-full object-contain p-6"/>
              </div>
              <div className="p-4">
                <p className="text-xs text-zinc-500 mb-2">{new Date(n.date).toLocaleDateString('en-GB', { day:'numeric', month:'long', year:'numeric'})}</p>
                <h2 className="font-semibold text-white mb-2 leading-snug">{n.title}</h2>
                <p className="text-zinc-400 text-sm leading-relaxed mb-4 line-clamp-2">{n.excerpt}</p>
                <div className="flex gap-2">
                  <button onClick={() => openEdit(n)} className="flex-1 inline-flex items-center justify-center gap-1.5 border border-zinc-800 text-zinc-300 hover:border-zinc-600 px-3 py-1.5 rounded text-sm"><Pencil size={14}/> Edit</button>
                  <button onClick={() => handleDelete(n)} className="inline-flex items-center justify-center gap-1.5 border border-zinc-800 text-zinc-400 hover:text-red-400 hover:border-red-400/50 px-3 py-1.5 rounded text-sm"><Trash2 size={14}/></button>
                </div>
              </div>
            </div>
          ))}
          {items.length === 0 && (
            <div className="col-span-full py-12 text-center text-zinc-500">No news articles yet.</div>
          )}
        </div>
      )}

      {editorOpen && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center px-4 py-10 overflow-y-auto">
          <form onSubmit={handleSave} className="bg-zinc-950 border border-zinc-800 rounded-lg w-full max-w-2xl p-6 my-auto">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-xl font-bold">{editing ? 'Edit article' : 'Add article'}</h2>
              <button type="button" onClick={() => setEditorOpen(false)} className="text-zinc-400 hover:text-white"><X size={20}/></button>
            </div>
            {error && <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 text-red-400 text-sm rounded">{error}</div>}
            <div className="space-y-4">
              <div>
                <label className="text-zinc-400 text-xs uppercase tracking-wider block mb-1.5">Title</label>
                <input required value={form.title} onChange={(e) => setForm({...form, title: e.target.value})} className={inputCls} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-zinc-400 text-xs uppercase tracking-wider block mb-1.5">Date</label>
                  <input required type="date" value={form.date} onChange={(e) => setForm({...form, date: e.target.value})} className={inputCls} />
                </div>
                <div>
                  <label className="text-zinc-400 text-xs uppercase tracking-wider block mb-1.5">Image URL</label>
                  <input required value={form.image} onChange={(e) => setForm({...form, image: e.target.value})} className={inputCls} />
                </div>
              </div>
              <div>
                <label className="text-zinc-400 text-xs uppercase tracking-wider block mb-1.5">Excerpt</label>
                <textarea required rows={4} value={form.excerpt} onChange={(e) => setForm({...form, excerpt: e.target.value})} className={`${inputCls} resize-none`} />
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6 pt-5 border-t border-zinc-900">
              <button type="button" onClick={() => setEditorOpen(false)} className="px-5 py-2.5 text-zinc-400 hover:text-white">Cancel</button>
              <button disabled={saving} className="inline-flex items-center gap-2 bg-[#909090] hover:bg-[#a8a8a8] text-black font-semibold px-6 py-2.5 rounded transition-colors disabled:opacity-60">
                {saving ? <><Loader2 className="animate-spin" size={16}/> Saving...</> : (editing ? 'Save' : 'Publish')}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default AdminNews;
